// features/reports/components/MiEscenario.tsx
// ============================================================================
// «MI ESCENARIO» del Pronóstico (Mabel, WhatsApp 23-oct-2026 12:35): «igual se puede hacer por separado como otro
// escenario y si hay manera de que lo pueda editar, para jugar con los números».
//
// Solo meses FUTUROS. Al lado del pronóstico automático (que no se toca): Pronóstico | Mi escenario | Diferencia.
// Arranca con las cifras del pronóstico; ella edita mensualidades (nº de alumnos × promedio), cada ingreso, la nómina
// (horario y comisión) y cada gasto presupuestado (+ «otros gastos»). El resultado se recalcula al teclear y se guarda
// solo lo editado (PUT /finances/presupuesto/escenario, por mes y estudio). «Restablecer» por campo o todo.
// La cuenta es la MISMA que lib/letsmoveEscenario.js:calcularEscenario (la del Excel): si cambias una, cambia la otra.
// ============================================================================
import { useEffect, useMemo, useRef, useState } from 'react';
import { RotateCcw, SlidersHorizontal, Check, AlertTriangle } from 'lucide-react';
import { moneyEntero as money, guardarEscenario, borrarEscenario, PresupuestoPronostico, PresupuestoEscenario, EscenarioOv } from '../../finances/api';

const r2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
const signo = (n: number) => (Math.abs(n) < 0.005 ? '—' : (n > 0 ? '+' : '−') + money(Math.abs(n)));
const colorDif = (n: number, gasto: boolean) => (Math.abs(n) < 0.005 ? 'text-content-3' : (n > 0) !== gasto ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400');

interface Fila { campo: string; label: string; sub?: string; pronostico: number; escenario: number; editado: boolean; gasto: boolean;
  alumnos?: { n: number; n0: number; promedio: number } }

/** El escenario calculado = pronóstico + overrides (espejo de calcularEscenario del backend). */
function calcular(p: PresupuestoPronostico, ov: EscenarioOv) {
  const has = (k: string) => Object.prototype.hasOwnProperty.call(ov, k);
  const ingresos: Fila[] = p.ingresos.porConcepto.map((x) => {
    if (x.k === 'mensualidad') {
      const n0 = Number(x.n) || 0, prom = n0 ? r2(x.monto / n0) : 0, c = 'ing:mensualidad:n';
      const n = has(c) ? ov[c] : n0;
      return { campo: c, label: `${x.categoria} · ${x.concepto}`, pronostico: r2(x.monto), escenario: has(c) ? r2(n * prom) : r2(x.monto), editado: has(c), gasto: false,
        alumnos: { n, n0, promedio: prom } };
    }
    const c = `ing:${x.k}`;
    return { campo: c, label: `${x.categoria} · ${x.concepto}`, pronostico: r2(x.monto), escenario: has(c) ? ov[c] : r2(x.monto), editado: has(c), gasto: false };
  });
  const nomina: Fila[] = ([['nom:horario', 'Horario × tarifas y sueldo fijo', p.nomina.horario], ['nom:comision', 'Comisión estimada', p.nomina.comision]] as const)
    .map(([c, label, v]) => ({ campo: c, label, pronostico: r2(v), escenario: has(c) ? ov[c] : r2(v), editado: has(c), gasto: true }));
  const gastos: Fila[] = p.gastos.detalle.map((g) => {
    const c = `gas:${g.key || `${g.categoria}|${g.concepto}`}`;
    return { campo: c, label: g.concepto, sub: `${g.apartado} · ${g.categoria}`, pronostico: r2(g.monto), escenario: has(c) ? ov[c] : r2(g.monto), editado: has(c), gasto: true };
  });
  gastos.push({ campo: 'gas:extra', label: 'Otros gastos', sub: 'lo que quieras sumar (en el pronóstico es $0)', pronostico: 0,
    escenario: has('gas:extra') ? ov['gas:extra'] : 0, editado: has('gas:extra'), gasto: true });
  const suma = (a: Fila[], f: 'pronostico' | 'escenario') => r2(a.reduce((s, x) => s + x[f], 0));
  const tot = { ingresos: { p: suma(ingresos, 'pronostico'), e: suma(ingresos, 'escenario') }, nomina: { p: suma(nomina, 'pronostico'), e: suma(nomina, 'escenario') },
    gastos: { p: suma(gastos, 'pronostico'), e: suma(gastos, 'escenario') } };
  const resultado = { p: r2(tot.ingresos.p - tot.nomina.p - tot.gastos.p), e: r2(tot.ingresos.e - tot.nomina.e - tot.gastos.e) };
  const editados = [...ingresos, ...nomina, ...gastos].filter((x) => x.editado).length;
  return { ingresos, nomina, gastos, tot, resultado, editados };
}

// Renglón: en escritorio, una rejilla de 5 columnas; en el celular, el nombre arriba y debajo Pronóstico | Mi escenario | Dif.
const REJILLA = 'sm:grid sm:grid-cols-[minmax(0,1fr)_8.5rem_10rem_8rem_2.25rem] sm:items-center sm:gap-3';
const SUBREJ = 'mt-1.5 grid grid-cols-[1fr_1.2fr_1fr_2.25rem] items-center gap-2 sm:mt-0 sm:contents';
const mini = 'block text-[10px] font-medium uppercase tracking-wide text-content-3 sm:hidden';

function Celda({ f, onSet, onReset, soloLectura }: { f: Fila; onSet: (campo: string, v: number | null) => void; onReset: (campo: string) => void; soloLectura: boolean }) {
  const valor = f.alumnos ? f.alumnos.n : f.escenario;
  const [txt, setTxt] = useState<string>(String(valor));
  const enfocado = useRef(false);
  // Si el valor cambia por fuera (restablecer, carga del servidor) y no lo está tecleando, se refleja.
  useEffect(() => { if (!enfocado.current) setTxt(String(valor)); }, [valor]);
  const aplicar = (s: string) => {
    const limpio = s.replace(/[$,\s]/g, '');
    if (limpio === '') return;
    const n = Number(limpio);
    if (!Number.isFinite(n) || n < 0) return;
    onSet(f.campo, f.alumnos ? Math.round(n) : r2(n));
  };
  return (
    <div className={`border-b border-line/60 py-2 ${REJILLA}`}>
      <div className="min-w-0 text-sm text-content-2">
        <span className={f.editado ? 'font-semibold text-content' : ''}>{f.label}</span>
        {f.sub && <span className="block text-xs text-content-3">{f.sub}</span>}
        {f.alumnos && <span className="block text-xs text-content-3">alumnos × {money(f.alumnos.promedio)} promedio (pronóstico: {f.alumnos.n0})</span>}
      </div>
      <div className={SUBREJ}>
        <div className="text-right text-sm tabular-nums text-content-3"><span className={mini}>Pronóstico</span>{money(f.pronostico)}</div>
        <div>
          <span className={mini}>{f.alumnos ? 'Mis alumnos' : 'Mi escenario'}</span>
          <div className="relative">
            {!f.alumnos && <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-sm text-content-3">$</span>}
            <input inputMode={f.alumnos ? 'numeric' : 'decimal'} value={txt} disabled={soloLectura}
              aria-label={`${f.label}: ${f.alumnos ? 'alumnos' : 'monto'} de mi escenario`}
              onFocus={(e) => { enfocado.current = true; e.target.select(); }}
              onChange={(e) => { setTxt(e.target.value); aplicar(e.target.value); }}
              onBlur={() => { enfocado.current = false; if (txt.replace(/[$,\s]/g, '') === '') onReset(f.campo); else setTxt(String(f.alumnos ? f.alumnos.n : f.escenario)); }}
              onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') { onReset(f.campo); (e.target as HTMLInputElement).blur(); } }}
              className={`w-full rounded-lg border bg-surface py-1.5 pr-2 text-right text-base tabular-nums outline-none transition focus:border-brand-blue sm:text-sm ${f.alumnos ? 'pl-2' : 'pl-5'} ${f.editado ? 'border-amber-400 bg-amber-50 font-semibold text-content dark:border-amber-600 dark:bg-amber-950/30' : 'border-line text-content'} disabled:opacity-60`} />
          </div>
          {f.alumnos && f.editado && <span className="mt-0.5 block text-right text-xs tabular-nums text-content-2">= {money(f.escenario)}</span>}
        </div>
        <div className={`text-right text-sm tabular-nums ${colorDif(f.escenario - f.pronostico, f.gasto)}`}><span className={mini}>Diferencia</span>{signo(f.escenario - f.pronostico)}</div>
        <div className="flex justify-end">
          {f.editado && !soloLectura && (
            <button onClick={() => onReset(f.campo)} title="Restablecer al pronóstico" aria-label={`Restablecer ${f.label}`}
              className="rounded-md p-1.5 text-content-3 transition hover:bg-surface-2 hover:text-content"><RotateCcw size={15} /></button>
          )}
        </div>
      </div>
    </div>
  );
}

function Total({ label, p, e, gasto, fuerte }: { label: string; p: number; e: number; gasto: boolean; fuerte?: boolean }) {
  return (
    <div className={`py-2 ${REJILLA} ${fuerte ? '' : 'border-b border-line'}`}>
      <div className={`text-sm ${fuerte ? 'font-bold text-content' : 'font-semibold text-content'}`}>{label}</div>
      <div className={SUBREJ}>
        <div className="text-right text-sm font-semibold tabular-nums text-content-2"><span className={mini}>Pronóstico</span>{money(p)}</div>
        <div className={`text-right tabular-nums ${fuerte ? 'font-display text-lg font-bold' : 'text-sm font-semibold'} ${fuerte ? (e >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400') : 'text-content'}`}><span className={mini}>Mi escenario</span>{money(e)}</div>
        <div className={`text-right text-sm font-semibold tabular-nums ${colorDif(e - p, gasto)}`}><span className={mini}>Diferencia</span>{signo(e - p)}</div>
        <div />
      </div>
    </div>
  );
}

export default function MiEscenario({ mes, pr, escenario, onGuardado }: { mes: string; pr: PresupuestoPronostico; escenario: PresupuestoEscenario | null | undefined;
  onGuardado?: (ov: EscenarioOv) => void }) {
  const [ov, setOv] = useState<EscenarioOv>(() => ({ ...(escenario?.ov || {}) }));
  const [estado, setEstado] = useState<'' | 'guardando' | 'guardado' | 'error' | 'sinPermiso'>('');
  const [msg, setMsg] = useState('');
  const sucio = useRef(false);
  const t = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Lo que llega del servidor (o de la caché local) manda mientras ella no haya tocado nada.
  useEffect(() => { if (!sucio.current) setOv({ ...(escenario?.ov || {}) }); }, [escenario?.updated_at, mes]); // eslint-disable-line react-hooks/exhaustive-deps

  const c = useMemo(() => calcular(pr, ov), [pr, ov]);

  const guardar = (nuevo: EscenarioOv) => {
    sucio.current = true;
    setOv(nuevo);
    if (t.current) clearTimeout(t.current);
    setEstado('guardando');
    t.current = setTimeout(async () => {
      try {
        if (Object.keys(nuevo).length) await guardarEscenario(mes, nuevo); else await borrarEscenario(mes);
        setEstado('guardado'); onGuardado?.(nuevo);
      } catch (e: any) {
        const s = String(e?.message || '');
        if (e?.status === 403 || /permiso|403|forbidden/i.test(s)) { setEstado('sinPermiso'); setMsg('Tu permiso de Finanzas no deja guardar el escenario: puedes jugar con los números, pero no se guardan.'); }
        else { setEstado('error'); setMsg(s || 'No se pudo guardar'); }
      }
    }, 700);
  };
  useEffect(() => () => { if (t.current) clearTimeout(t.current); }, []);

  const setCampo = (campo: string, v: number | null) => {
    const n = { ...ov };
    if (v == null) delete n[campo]; else n[campo] = v;
    guardar(n);
  };
  const resetCampo = (campo: string) => { if (campo in ov) setCampo(campo, null); };
  const resetTodo = () => { if (window.confirm('¿Restablecer todo «Mi escenario» a las cifras del pronóstico?')) guardar({}); };

  const filas = (arr: Fila[]) => arr.map((f) => <Celda key={f.campo} f={f} onSet={setCampo} onReset={resetCampo} soloLectura={false} />);
  const seccion = (titulo: string) => <div className="mb-0.5 mt-4 text-xs font-semibold uppercase tracking-wide text-content-3">{titulo}</div>;

  return (
    <div className="mt-4 rounded-2xl border border-brand-blue/40 bg-surface p-4 shadow-sm" data-qa="mi-escenario">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="flex items-center gap-2 font-display text-base font-bold text-content"><SlidersHorizontal size={16} className="text-brand-blue" /> Mi escenario de {pr.titulo.toLowerCase()}</h3>
          <p className="mt-0.5 text-xs text-content-3">Cambia cualquier cifra para ver qué pasaría. El pronóstico de arriba no cambia; aquí solo se guarda lo que edites.</p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          {estado === 'guardando' && <span className="text-content-3">Guardando…</span>}
          {estado === 'guardado' && <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400"><Check size={13} /> Guardado</span>}
          {c.editados > 0 && (
            <button onClick={resetTodo} className="flex items-center gap-1.5 rounded-lg border border-line bg-surface px-2.5 py-1.5 text-sm text-content-2 transition hover:bg-surface-2">
              <RotateCcw size={14} /> Restablecer todo
            </button>
          )}
        </div>
      </div>
      {(estado === 'error' || estado === 'sinPermiso') && (
        <p className="mt-2 flex items-start gap-1.5 rounded-lg bg-amber-50 p-2 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"><AlertTriangle size={14} className="mt-0.5 shrink-0" /> {msg}</p>
      )}

      {/* Encabezado de columnas (solo escritorio; en el celular cada cifra trae su rótulo). */}
      <div className={`mt-3 hidden border-b border-line pb-1.5 text-xs font-medium uppercase tracking-wide text-content-3 ${REJILLA}`}>
        <div>Concepto</div><div className="text-right">Pronóstico</div><div className="text-right">Mi escenario</div><div className="text-right">Diferencia</div><div />
      </div>

      {seccion('Ingresos')}
      {filas(c.ingresos)}
      <Total label="Total ingresos" p={c.tot.ingresos.p} e={c.tot.ingresos.e} gasto={false} />
      {seccion('Nómina')}
      {filas(c.nomina)}
      <Total label="Total nómina" p={c.tot.nomina.p} e={c.tot.nomina.e} gasto />
      {seccion('Gastos')}
      {filas(c.gastos)}
      <Total label="Total gastos" p={c.tot.gastos.p} e={c.tot.gastos.e} gasto />

      <div className="mt-3 rounded-xl bg-surface-2/70 px-3">
        <Total label="Resultado (ingresos − nómina − gastos)" p={c.resultado.p} e={c.resultado.e} gasto={false} fuerte />
      </div>
      <p className="mt-2 text-xs text-content-3">
        {c.editados ? `${c.editados} cifra${c.editados === 1 ? '' : 's'} editada${c.editados === 1 ? '' : 's'} (en amarillo); lo demás sigue al pronóstico y se actualiza solo. ` : 'Aún no has cambiado nada: Mi escenario = pronóstico. '}
        Mensualidades: escribe cuántos alumnos; se multiplica por el promedio por alumno del pronóstico. Para volver al pronóstico usa <RotateCcw size={11} className="inline" /> o borra la cifra. También sale en el Excel (hoja «Mi escenario»).
      </p>
    </div>
  );
}
